import {Link, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/store';
import styles from './notification.module.scss';
import {markNotificationRead} from '../../store/slices/notifications_slice';
import {format, formatDistanceToNow} from 'date-fns';
import {useEffect} from 'react';
import {Main} from '../../components/main/main.tsx';
import {Icon, IconSize} from '../../components/icon/icon.tsx';
import {Button} from '../../components/button/button.tsx';
import {__} from '../../support/lang/translations.ts';
import {ButtonPosition} from '../../enums/button_position.ts';
import {FlexColumn} from '../../components/utility/flex_column.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {ShowElement} from '../../components/utility/show_element_wrapper.tsx';
import {HideElement} from '../../components/utility/hide_element_wrapper.tsx';
import {FlexRow} from '../../components/utility/flex_row.tsx';
import {Spacer} from '../../components/utility/spacer.tsx';

export enum NotificationAction {
    Appointment = 'make_appointment',
    Onboarding = 'open_onboarding',
}
import {Identifier} from '../../components/identifier/identifier.tsx';
import {brandLogo} from '../../support/brand_logo.ts';

export enum NotificationType {
    General = 'general',
    Vehicle = 'vehicle',
}
export function Notification() {
    const params = useParams<'notificationId'>();
    const notificationId = parseInt(params.notificationId ?? '-1', 10);
    const notification = useAppSelector((state) =>
        state.notifications.notifications?.find((n) => notificationId != null && n?.id === notificationId),
    );
    const vehicleId = notification?.vehicle_id;
    const token = useAppSelector((state) => state.exchangeToken.token);
    const appConfig = useAppSelector((state) => state.appConfig.appconfig);
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const vehicle = customerInfo.me?.customer?.vehicles?.find((v) => v?.id === vehicleId);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (notification?.read === false && token && notification.id != null) {
            dispatch(markNotificationRead({token, notificationId: notification.id}));
        }
    }, [notification, dispatch, token]);

    if (!notification) {
        return <div>404...</div>;
    }

    const getActionUrl = (): string | null => {
        switch (notification.action) {
            case NotificationAction.Appointment:
                return '/appointment';
            case NotificationAction.Onboarding:
                return '/onboarding';
            default:
                return null;
        }
    };

    const getActionIcon = (): string => {
        switch (notification.action) {
            case NotificationAction.Appointment:
                return 'fa-regular fa-calendar';
            case NotificationAction.Onboarding:
                return 'fa-regular fa-user';
            default:
                return '';
        }
    };

    const actionUrl: string | null = getActionUrl();
    const emailSettings = notification?.emailSettings ?? null;
    const icon = notification?.icon ?? null;
    const iconBackGroundColour = notification?.icon_colour_background ?? null;
    const socialMedia = emailSettings?.socialMedia || {};
    const socialKeys = ['facebook', 'instagram', 'linkedin', 'snapchat', 'tiktok', 'twitter', 'youtube'];
    const hasAnySocial = socialKeys.some((key) => {
        const value = socialMedia[key as keyof typeof socialMedia];
        return typeof value === 'string' && value.trim() !== '';
    });
    console.log('notfication', notification);
    console.log('hasAnySocial', hasAnySocial);

    return (
        <Main className={styles.main}>
            <div className={styles.notification}>
                <div className={styles.notification__title}>
                    <div className={styles.title__buttons}>
                        <FlexColumn>
                            <Button to="/notifications" position={ButtonPosition.Right} noBorder={true}>
                                <i className="fa-light fa-chevron-left" />
                                {__('notifications.buttons.back')}
                            </Button>
                        </FlexColumn>
                        <FlexColumn alignment={Alignment.End}>
                            <Button to={`/chat?notificationId=${notificationId}`}>
                                <i className="fa-regular fa-comment" />
                                {__('notifications.buttons.reply')}
                            </Button>
                        </FlexColumn>
                    </div>
                    <HideElement size={'md'}>
                        {vehicle != null ? (
                            <Icon src={brandLogo(vehicle.brand)} alt={vehicle.brand ?? ''} size={IconSize.Large} />
                        ) : (
                            <Icon iconClassName={'fa-light fa-user'} size={IconSize.Large} />
                        )}
                    </HideElement>
                    <ShowElement size={'md'}>
                        {vehicle != null ? (
                            <Icon src={brandLogo(vehicle.brand)} alt={vehicle.brand ?? ''} size={IconSize.Normal} />
                        ) : (
                            <Icon iconClassName={'fa-light fa-user'} size={IconSize.Normal} />
                        )}
                    </ShowElement>
                    <div className={styles.title__wrapper}>
                        <h2>{notification?.title}</h2>
                        {vehicle != null && (
                            <h3>
                                <span>{`${vehicle.brand} ${vehicle.model} `}</span>
                                <Identifier
                                    identifier={
                                        vehicle.identifiers?.find((id) => id?.type === 'license_plate')?.value ?? ''
                                    }
                                />
                            </h3>
                        )}
                    </div>
                </div>
                <div className={styles.notification__content}>
                    <div className={styles.content__title}>
                        {appConfig?.appicon != null && <Icon src={appConfig?.appicon ?? ''} />}
                        <div className={styles.title__wrapper}>
                            {appConfig?.appname != null && <h3>{appConfig?.appname}</h3>}
                            {notification?.created_at_iso8601 && (
                                <small className="text-muted">{`${format(new Date(notification.created_at_iso8601), 'dd MMM yyyy, HH:mm')} ${formatDistanceToNow(new Date(notification.created_at_iso8601))}`}</small>
                            )}
                        </div>
                    </div>
                    <div
                        className={styles.content__content}
                        style={{backgroundColor: emailSettings?.background_colour ?? '#FAFAFA'}}
                    >
                        <div className={styles.content}>
                            {emailSettings?.logo_image_url && emailSettings.logo_image_url.trim() !== '' && (
                                <div className={styles.content_logo}>
                                    <img
                                        src={emailSettings?.logo_image_url ?? ''}
                                        alt={'Logo'}
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                    />
                                </div>
                            )}
                            {emailSettings?.logo_image_url && emailSettings.logo_image_url.trim() !== '' && (
                                <div className={styles.content_header}>
                                    <img
                                        src={emailSettings?.header_image_url ?? ''}
                                        alt={'Header image'}
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                    />
                                </div>
                            )}

                            <div className={styles.content_row}>
                                {icon != null && (
                                    <div
                                        className={styles.content_icon}
                                        style={
                                            iconBackGroundColour ? {backgroundColor: iconBackGroundColour} : undefined
                                        }
                                    >
                                        <img
                                            src={icon}
                                            alt={'icon'}
                                            onError={(e) => (e.currentTarget.style.display = 'none')}
                                        />
                                    </div>
                                )}
                                {notification?.message_title && notification.message_title.trim() !== '' && (
                                    <p
                                        className={styles.message_title}
                                        style={{color: notification?.message_title_colour ?? '#000000'}}
                                    >
                                        {notification?.message_title}
                                    </p>
                                )}
                            </div>

                            {notification?.message && notification.message.trim() !== '' && (
                                <p className={styles.notification_text}>{notification?.message}</p>
                            )}

                            {actionUrl != null && (
                                <>
                                    <Spacer style={{height: '2rem'}} />
                                    <FlexRow alignment={Alignment.Center}>
                                        <Link
                                            to={actionUrl}
                                            className={styles.notification_button}
                                            style={{
                                                color: emailSettings?.button_text_colour ?? '#ffffff',
                                                backgroundColor: emailSettings?.button_colour ?? '#ffffff',
                                            }}
                                        >
                                            <i className={getActionIcon()} />
                                            <span>{__(`notifications.buttons.${notification.action}`)}</span>
                                        </Link>
                                    </FlexRow>
                                </>
                            )}

                            {emailSettings != null && (
                                <div
                                    style={{
                                        backgroundColor: emailSettings?.footer_background_colour ?? '#ffffff',
                                        padding: '20px',
                                    }}
                                >
                                    <h1
                                        className={styles.footer_title}
                                        style={{color: emailSettings?.footer_text_colour ?? '#000000'}}
                                    >
                                        Meer informatie
                                    </h1>

                                    {emailSettings?.phone_number != null && (
                                        <div className={styles.footer_row}>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                Algemeen nummer
                                            </p>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                <a
                                                    href={`tel:${emailSettings?.phone_number}`}
                                                    style={{color: 'inherit', textDecoration: 'none'}}
                                                >
                                                    {emailSettings?.phone_number}
                                                </a>
                                            </p>
                                        </div>
                                    )}

                                    {emailSettings?.email_address != null && (
                                        <div className={styles.footer_row}>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                Algemeen e-mailadres
                                            </p>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                <a
                                                    href={`mailto:${emailSettings?.email_address}`}
                                                    style={{color: 'inherit', textDecoration: 'none'}}
                                                >
                                                    {emailSettings?.email_address}
                                                </a>
                                            </p>
                                        </div>
                                    )}

                                    {emailSettings?.website != null && (
                                        <div className={styles.footer_row}>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                Website
                                            </p>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                <a
                                                    href={
                                                        emailSettings?.website?.startsWith('http')
                                                            ? emailSettings.website
                                                            : `https://${emailSettings.website}`
                                                    }
                                                    style={{color: 'inherit', textDecoration: 'none'}}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {emailSettings?.website}
                                                </a>
                                            </p>
                                        </div>
                                    )}

                                    {hasAnySocial && (
                                        <div className={styles.footer_row}>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                Socials
                                            </p>
                                            <div className={styles.social_icons_container}>
                                                {emailSettings?.socialMedia?.facebook &&
                                                    emailSettings.socialMedia.facebook.trim() !== '' && (
                                                        <div className={styles.social_icon}>
                                                            <a
                                                                href={emailSettings?.socialMedia?.facebook}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src="/images/icons/facebook.png"
                                                                    alt="facebook icon"
                                                                    onError={(e) =>
                                                                        (e.currentTarget.style.display = 'none')
                                                                    }
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                                {emailSettings?.socialMedia?.instagram &&
                                                    emailSettings.socialMedia.instagram.trim() !== '' && (
                                                        <div className={styles.social_icon}>
                                                            <a
                                                                href={emailSettings?.socialMedia?.instagram}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src="/images/icons/instagram.png"
                                                                    alt="instagram icon"
                                                                    onError={(e) =>
                                                                        (e.currentTarget.style.display = 'none')
                                                                    }
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                                {emailSettings?.socialMedia?.linkedin &&
                                                    emailSettings.socialMedia.linkedin.trim() !== '' && (
                                                        <div className={styles.social_icon}>
                                                            <a
                                                                href={emailSettings?.socialMedia?.linkedin}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src="/images/icons/linkedin.png"
                                                                    alt="linkedin icon"
                                                                    onError={(e) =>
                                                                        (e.currentTarget.style.display = 'none')
                                                                    }
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                                {emailSettings?.socialMedia?.snapchat &&
                                                    emailSettings.socialMedia.snapchat.trim() !== '' && (
                                                        <div className={styles.social_icon}>
                                                            <a
                                                                href={emailSettings?.socialMedia?.snapchat}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src="/images/icons/snapchat.png"
                                                                    alt="snapchat icon"
                                                                    onError={(e) =>
                                                                        (e.currentTarget.style.display = 'none')
                                                                    }
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                                {emailSettings?.socialMedia?.twitter &&
                                                    emailSettings.socialMedia.twitter.trim() !== '' && (
                                                        <div className={styles.social_icon}>
                                                            <a
                                                                href={emailSettings?.socialMedia?.twitter}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src="/images/icons/twitter.png"
                                                                    alt="twitter icon"
                                                                    onError={(e) =>
                                                                        (e.currentTarget.style.display = 'none')
                                                                    }
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                                {emailSettings?.socialMedia?.youtube &&
                                                    emailSettings.socialMedia.youtube.trim() !== '' && (
                                                        <div className={styles.social_icon}>
                                                            <a
                                                                href={emailSettings?.socialMedia?.youtube}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src="/images/icons/youtube.png"
                                                                    alt="youtube icon"
                                                                    onError={(e) =>
                                                                        (e.currentTarget.style.display = 'none')
                                                                    }
                                                                />
                                                            </a>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                    {emailSettings?.address && emailSettings?.address.trim() !== '' && (
                                        <div className={styles.footer_row}>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                Adres gegevens
                                            </p>
                                            <p style={{color: emailSettings?.footer_text_colour ?? '#000000'}}>
                                                {emailSettings?.address}
                                            </p>
                                        </div>
                                    )}

                                    {emailSettings?.address && emailSettings?.address.trim() !== '' && (
                                        <div className={styles.footer_row}>
                                            <p></p>
                                            <div className={styles.route_button}>
                                                <Button
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(emailSettings.address)}`}
                                                    target="_blank"
                                                    customColor={emailSettings?.footer_text_colour ?? '#000000'}
                                                >
                                                    <i className="fa-solid fa-route" />
                                                    Route
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {emailSettings?.footer_image_url && emailSettings?.footer_image_url.trim() !== '' && (
                                <div className={styles.content_footer}>
                                    <img
                                        src={emailSettings?.footer_image_url}
                                        alt={'Footer image'}
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                    />
                                </div>
                            )}

                            {emailSettings?.logo_image_url && emailSettings?.logo_image_url.trim() !== '' && (
                                <div className={styles.content_logo}>
                                    <img
                                        src={emailSettings?.logo_image_url}
                                        alt={'Logo'}
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.notification__footer}>
                    <Button to={`/chat?notificationId=${notificationId}`}>
                        <i className="fa-regular fa-comment" />
                        {__('notifications.buttons.reply')}
                    </Button>
                </div>
            </div>
        </Main>
    );
}
